import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icons } from "../lib/Icons";

export default function SchoolDemoPage() {
  const { pathname } = useLocation();
  const shareUrl = `${window.location.origin}${pathname}`;
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: "SEN-Education platform demo",
        text: "Watch a walkthrough of the SEN-Education platform for schools.",
        url: shareUrl,
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        handleCopyLink();
      }
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-indigo-50/40 via-slate-50 to-blue-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-indigo-950/40"
      style={{ fontFamily: "'Nunito', sans-serif" }}
    >
      <header className="border-b border-indigo-200/60 bg-white/90 backdrop-blur-lg dark:border-indigo-900/50 dark:bg-gray-900/80 shadow-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/brand/logo-thumbnail-blue.png"
              alt="SEN-Education"
              className="h-9 w-9 rounded-lg"
            />
            <span className="font-bold text-lg text-indigo-700 dark:text-indigo-300">SEN-Education</span>
          </Link>
          <Link
            to="/"
            className="rounded-xl border border-indigo-200 px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/50"
          >
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-indigo-600">Schools platform demo</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            See SEN-Education in action
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-gray-600 dark:text-gray-300 sm:text-lg">
            A walkthrough of the SEN-Education platform for schools, including curriculum planning,
            EHCP analysis, intervention tracking, and evidence-rich reporting.
          </p>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-indigo-200/60 bg-black shadow-2xl shadow-indigo-200/40 dark:border-indigo-800">
          <video
            className="aspect-video w-full"
            controls
            playsInline
            preload="metadata"
            poster="/brand/logo-thumbnail-blue.png"
          >
            <source src="/demo_final.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-white px-5 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 dark:border-indigo-800 dark:bg-gray-800 dark:text-indigo-300 dark:hover:bg-indigo-950/50"
          >
            {copied ? (
              <>
                <Icons.Check className="h-4 w-4 text-emerald-600" />
                Link copied
              </>
            ) : (
              <>
                <Icons.Link className="h-4 w-4" />
                Copy link
              </>
            )}
          </button>
          {canNativeShare && (
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            >
              Share
            </button>
          )}
        </div>
      </main>

      <footer className="border-t border-indigo-200/40 py-6 text-center dark:border-indigo-900/50">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          © 2026 The SEND Tech Company Ltd. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
